import type { ProductCard } from "@raumagarn/formatters";
import type { BlokProps } from "@trydig/storyblok";
import Image from "next/image";
import { Suspense, useMemo } from "react";
import { BlokWrap } from "~/components/bloks/BlokWrap";
import { ClientTag } from "~/components/bloks/TaggedImageBlok/ClientTag";
import { Skeleton } from "~/components/ui/Skeleton";
import type { TaggedImageStoryblokExtended } from "~/generated/sb/addtional-types";
import type { TaggedImageStoryblok } from "~/generated/sb/blok-types";
import { Link } from "~/i18n/routing";
import { getStoryblokHeight, getStoryblokWidth } from "~/lib/storyblok/mediaFormatters";
import type { BlokData } from "~/srpc/routes/story.router";
import styles from "./TaggedImageBlok.module.css";

const Tag = ({
	tag,
	data,
}: {
	tag: {
		x: number;
		y: number;
	};
	data?: ProductCard;
}) => {
	const product = data;

	if (!product) return null;

	return (
		<div
			className={styles.tag}
			style={{
				top: `${tag.y}%`,
				left: `${tag.x}%`,
			}}
		>
			<Suspense fallback={<Skeleton height="50px" />}>
				<ClientTag product={product} />
			</Suspense>
		</div>
	);
};

export const TaggedImageBlok = ({
	blok,
	data,
	sizes = "100vw",
}: BlokProps<TaggedImageStoryblok, BlokData["taggedImage"]> & {
	sizes?: string;
}) => {
	const extendedBlok = blok as TaggedImageStoryblokExtended;

	const tags = useMemo(
		() =>
			extendedBlok?.products?.products?.map((product, i) => {
				return {
					x: product.tag.x,
					y: product.tag.y,
					product: data?.[i],
				};
			}),
		[extendedBlok.products.products, data],
	);

	if (!extendedBlok.products.media) return null;

	const media = {
		file: extendedBlok?.products?.media?.filename,
		alt: "",
		width: +(getStoryblokWidth(extendedBlok?.products?.media) ?? 0),
		height: +(getStoryblokHeight(extendedBlok?.products?.media) ?? 0),
		focus: undefined,
		_uid: extendedBlok._uid,
	};

	if (!media.file) return null;

	const singleProductHref = tags?.length === 1 ? tags[0].product?.uri : undefined;

	const image = (
		<Image
			src={media.file}
			alt={media.alt}
			fill={false}
			width={media.width}
			height={media.height}
			sizes={sizes}
			className={styles.image}
			style={{
				objectPosition: media.focus,
				aspectRatio: `${media.width}/${media.height}`,
			}}
		/>
	);

	return (
		<BlokWrap asChild editable={blok}>
			<div className={styles.wrapper}>
				{singleProductHref ? <Link href={singleProductHref}>{image}</Link> : image}

				{tags?.map((product, i) => {
					return (
						<Tag
							key={blok?._uid + i + product?.product?.name}
							tag={product}
							data={product?.product}
						/>
					);
				})}
			</div>
		</BlokWrap>
	);
};
